import { Combobox } from '@base-ui/react/combobox';
import { merge } from 'lodash-es';
import * as React from 'react';
import { mergeRefs } from 'react-merge-refs';

import type { IconName } from '../../assets/Icon/Icon';
import type {
  ControlAppearance,
  ControlMaxWidth,
  ControlMinWidth,
  ControlSize,
  ControlVariantInput,
  ControlWidth,
} from '../../controls/shared/types';
import type { ComboBoxPlacement, ComboBoxRow, ComboBoxRowGroup } from '../shared/ComboBox';
import { Button } from '../../controls/Button/Button';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import {
  useControlAppearance,
  useControlGhost,
  useControlMaxWidth,
  useControlMinWidth,
  useControlSize,
  useControlVariant,
  useControlWidth,
} from '../../controls/shared/context';
import { styled } from '../../stitches.config';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { useControllableState } from '../../utilities/useControllableState';
import { SelectValue } from '../Select/Select';
import {
  COMBOBOX_MAX_ITEMS,
  ComboBoxActions,
  ComboBoxEmpty,
  ComboBoxGroup,
  ComboBoxGroupLabel,
  comboBoxIsItemEqualToValue,
  ComboBoxList,
  ComboBoxListOption,
  comboBoxPlacement,
  ComboBoxPopup,
  ComboBoxPortal,
  ComboBoxPositioner,
  comboBoxRowLabel,
  ComboBoxSearch,
  ComboBoxTrigger,
  ComboBoxTriggerArrows,
  ComboBoxTriggerIcon,
  useComboBoxCollection,
  useComboBoxFilter,
  useComboBoxLabel,
  useValueSet,
} from '../shared/ComboBox';

export { ComboBoxItem, ComboBoxSection } from '../shared/ComboBox';

/** What `renderSelected` receives. */
export type ComboBoxNode = ComboBoxRow;

/**
 * Targetable so an option can style itself differently inside the popup to how it reads in the
 * trigger. See `HideInComboBoxPopover`.
 */
export const ComboBoxPopover = styled(ComboBoxPopup, {
  // Wide enough for a long label to wrap rather than stretch the popup across the viewport.
  maxWidth: 400,
});
ComboBoxPopover.displayName = 'ComboBoxPopover';

const ComboBoxContainer = styled('div', {
  heightAll: 'min-content',
});
ComboBoxContainer.displayName = 'ComboBoxContainer';

const ComboBoxControlGroup = styled(ControlGroup, {
  width: '100%',
});
ComboBoxControlGroup.displayName = 'ComboBoxControlGroup';

const TRIGGER_CSS = { width: '100%', height: '100%' };

interface ComboBoxProps<T = object> {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /**
   * Rendered under the search field, for a hint or a filter control.
   */
  actions?: React.ReactNode;
  appearance?: ControlAppearance;
  /**
   * Adds a button that resets the value, and makes Backspace on the trigger do the same.
   */
  canClearValue?: boolean;
  /**
   * `ComboBoxItem` and `ComboBoxSection` elements, or a function mapping `items` onto them.
   */
  children?: React.ReactNode | ((item: T) => React.ReactElement);
  /**
   * Alias of `items`.
   */
  defaultItems?: Iterable<T>;
  defaultValue?: string;
  disabled?: boolean;
  /**
   * Values that can't be selected, on top of any `ComboBoxItem` marked `disabled`.
   */
  disabledKeys?: Iterable<string | number>;
  ghost?: boolean;
  groupSeparators?: boolean;
  icon?: IconName;
  id?: string;
  /**
   * Shows an em-dash in place of a value, for a control standing in for a mixed selection.
   */
  indeterminate?: boolean;
  invalid?: boolean;
  /**
   * Options for a function child, in place of listing them statically.
   */
  items?: Iterable<T>;
  /**
   * Accessible name for the list of options. Falls back to `placeholder`.
   */
  label?: React.ReactNode;
  maxWidth?: ControlMaxWidth;
  /**
   * Withholds results until the search reaches this many characters, for lists too long to render
   * unfiltered.
   */
  minSearchLength?: number;
  minWidth?: ControlMinWidth;
  /**
   * Submits the value with the surrounding form.
   */
  name?: string;
  placeholder?: string;
  placement?: ComboBoxPlacement;
  popoverMaxWidth?: string | number;
  ref?: React.RefObject<HTMLElement | null>;
  /**
   * Replaces the selected option's own content in the trigger.
   */
  renderSelected?: (option: ComboBoxNode) => React.ReactNode;
  searchPlaceholder?: string;
  size?: ControlSize;
  value?: string | null;
  variant?: ControlVariantInput;
  width?: ControlWidth;
  /**
   * Called as the search field changes, for a list fetched per query.
   */
  onInputChange?: (value: string) => void;
  onValueChange?: (value: string) => void;
}

export function ComboBox<T extends object = object>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  actions,
  appearance,
  canClearValue = false,
  children,
  defaultItems,
  defaultValue,
  disabled = false,
  disabledKeys,
  ghost,
  groupSeparators,
  icon,
  id,
  indeterminate,
  invalid,
  items,
  label,
  maxWidth,
  minSearchLength,
  minWidth,
  name,
  placeholder = 'Select an option',
  placement,
  popoverMaxWidth,
  ref,
  renderSelected,
  searchPlaceholder = 'Search…',
  size,
  value,
  variant,
  width,
  onInputChange,
  onValueChange,
}: ComboBoxProps<T>) {
  const controlAppearance = useControlAppearance(appearance, 'muted');
  const controlGhost = useControlGhost(ghost, false);
  const controlSize = useControlSize(size, 'medium');
  const controlVariant = useControlVariant(variant, 'neutral');
  const controlWidth = useControlWidth(width) ?? width;
  const controlMaxWidth = useControlMaxWidth(maxWidth) ?? maxWidth;
  const controlMinWidth = useControlMinWidth(minWidth) ?? minWidth;

  // Walking the descriptor tree and folding its text is the expensive half of a search and doesn't
  // depend on the query, so it happens once per `children` rather than once per keystroke.
  const collection = useComboBoxCollection(children, items ?? defaultItems);
  const disabledValues = useValueSet(disabledKeys);
  const matchesQuery = useComboBoxFilter();

  const handleValueChange = React.useCallback(
    (next: string | null) => onValueChange?.(next ?? ''),
    [onValueChange],
  );

  const [resolvedValue, setResolvedValue] = useControllableState<string | null>({
    defaultValue: defaultValue ?? null,
    onChange: handleValueChange,
    value,
  });

  const [query, setQuery] = React.useState('');
  const trimmedQuery = query.trim();

  // Base UI passes `filter` the query it seeds from the selected value's label rather than the one
  // being typed, so a reopened list would never reach past the row already chosen.
  const filter = React.useCallback(
    (row: unknown) => matchesQuery(row, query),
    [matchesQuery, query],
  );

  const handleInputValueChange = React.useCallback(
    (next: string) => {
      setQuery(next);
      onInputChange?.(next);
    },
    [onInputChange],
  );

  // The trigger renders a button, but callers hold the wider element type react-aria handed them.
  const setTriggerRef = React.useMemo(() => mergeRefs<HTMLElement>([ref ?? null]), [ref]);

  const { labelProps, labelNode } = useComboBoxLabel({
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    label,
    placeholder,
  });

  // Ghost styling is meant to lift as soon as the control is engaged, which a `:focus` rule can't
  // see: in input-inside-popup mode focus goes to the search field, not the trigger.
  const [open, setOpen] = React.useState(false);
  const showGhost = controlGhost && !open;

  // Base UI clears the query only once a close has finished, so reopening mid-exit keeps the last
  // search. Clearing on open makes it unconditional.
  const handleOpenChange = React.useCallback((next: boolean) => {
    if (next) setQuery('');
    setOpen(next);
  }, []);

  const selectedRow = resolvedValue == null ? undefined : collection.byValue.get(resolvedValue);

  let selectedLabel: React.ReactNode;
  if (indeterminate) {
    selectedLabel = <>&ndash;</>;
  } else if (!selectedRow) {
    selectedLabel = placeholder;
  } else {
    selectedLabel = renderSelected ? renderSelected(selectedRow) : selectedRow.label;
  }

  // Withholding the options rather than the rendered list is what makes `minSearchLength` worth
  // having: below the threshold Base UI has nothing to filter, and nothing to mount.
  const belowSearchLength = minSearchLength != null && trimmedQuery.length < minSearchLength;
  const searchableItems = React.useMemo(
    () => (belowSearchLength ? [] : collection.groups ?? collection.rows),
    [belowSearchLength, collection],
  );

  let emptyMessage: React.ReactNode = 'No results';
  if (belowSearchLength) {
    emptyMessage = `Type at least ${minSearchLength} ${
      minSearchLength === 1 ? 'character' : 'characters'
    } to see results.`;
  } else if (trimmedQuery) {
    emptyMessage = `No results matching “${trimmedQuery}”`;
  }

  const containerCSS = React.useMemo(
    () => merge(widthCSS(controlWidth), maxWidthCSS(controlMaxWidth), minWidthCSS(controlMinWidth)),
    [controlWidth, controlMaxWidth, controlMinWidth],
  );

  const popupStyle = React.useMemo(
    () => (popoverMaxWidth == null ? undefined : { maxWidth: popoverMaxWidth }),
    [popoverMaxWidth],
  );

  const { side, align } = React.useMemo(() => comboBoxPlacement(placement), [placement]);

  const renderRow = React.useCallback(
    (row: ComboBoxRow) => (
      <ComboBoxListOption
        key={row.value}
        row={row}
        disabled={row.disabled || disabledValues.has(row.value)}
      />
    ),
    [disabledValues],
  );

  const renderGroup = React.useCallback(
    (group: ComboBoxRowGroup) => (
      <ComboBoxGroup key={group.key} items={group.items} separated={groupSeparators}>
        {group.label != null && <ComboBoxGroupLabel>{group.label}</ComboBoxGroupLabel>}
        <Combobox.Collection>{renderRow}</Combobox.Collection>
      </ComboBoxGroup>
    ),
    [groupSeparators, renderRow],
  );

  // An explicit `Combobox.Collection` rather than a function child of the list: 1.3.0 caches the
  // first function child it sees in a module-level variable, so every combobox in the app would
  // render this one's rows. Memoizing the element also lets React bail out of the subtree when the
  // list re-renders for a highlight change — refiltering still lands, because it arrives through
  // context rather than through props.
  const listChildren = React.useMemo(
    () => <Combobox.Collection>{collection.groups ? renderGroup : renderRow}</Combobox.Collection>,
    [collection.groups, renderGroup, renderRow],
  );

  const trigger = (
    <ComboBoxTrigger
      ref={setTriggerRef}
      id={id}
      {...labelProps}
      size={controlSize}
      appearance={controlGhost ? 'muted' : controlAppearance}
      variant={controlVariant}
      ghost={showGhost}
      data-ghost={showGhost || undefined}
      isDisabled={disabled}
      invalid={invalid ?? false}
      css={TRIGGER_CSS}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Backspace' && canClearValue) setResolvedValue(null);
      }}
    >
      {icon && <ComboBoxTriggerIcon icon={icon} />}
      <SelectValue size={controlSize}>{selectedLabel}</SelectValue>
      <ComboBoxTriggerArrows icon="chevrons-vertical" />
    </ComboBoxTrigger>
  );

  return (
    <Combobox.Root<string, false>
      disabled={disabled}
      items={searchableItems}
      filter={filter}
      limit={COMBOBOX_MAX_ITEMS}
      autoHighlight
      itemToStringLabel={comboBoxRowLabel}
      isItemEqualToValue={comboBoxIsItemEqualToValue}
      inputValue={query}
      name={name}
      value={resolvedValue}
      onValueChange={setResolvedValue}
      onInputValueChange={handleInputValueChange}
      onOpenChange={handleOpenChange}
    >
      <ComboBoxContainer css={containerCSS}>
        {labelNode}
        {selectedRow && canClearValue ? (
          <ComboBoxControlGroup ghost={showGhost} size={controlSize}>
            {trigger}
            <Button
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setResolvedValue(null);
              }}
              type="button"
              appearance="muted"
              arrangement="hidden-label"
              ghost={showGhost}
              icon="cross"
              variant="secondary"
            >
              Clear
            </Button>
          </ComboBoxControlGroup>
        ) : (
          trigger
        )}
      </ComboBoxContainer>
      <ComboBoxPortal>
        <ComboBoxPositioner align={align} side={side} collisionPadding={8} sideOffset={4}>
          <ComboBoxPopover {...labelProps} style={popupStyle}>
            <ComboBoxSearch placeholder={searchPlaceholder} />
            {actions && <ComboBoxActions>{actions}</ComboBoxActions>}
            <ComboBoxList aria-label={typeof label === 'string' ? label : placeholder}>
              {listChildren}
            </ComboBoxList>
            <ComboBoxEmpty>{emptyMessage}</ComboBoxEmpty>
          </ComboBoxPopover>
        </ComboBoxPositioner>
      </ComboBoxPortal>
    </Combobox.Root>
  );
}

export type { ComboBoxProps };
